import {action, computed, observable} from 'mobx';
import {successAlert} from 'shared/tools';
import {endpoints, types, t} from 'shared/core';
import Signature from 'stores/signatures/Signature';
import mergeFields from 'stores/documents/mergeFields';
import {CompanyDocument, NotificationRecipient} from 'stores/documents';
import {CustomField} from 'stores/custom_fields';
import Folder from 'stores/folders/Folder';
import {DomainStore} from 'shared/store';
import _ from 'lodash';
import {User} from 'stores/users';

class CompanyDocumentEditState {
  store = new DomainStore();
  history;
  match;

  @observable companyDocument;
  @observable signatures = [];
  @observable errors = {};
  @observable folders = [];
  @observable users = [];

  receiveProps({history, match}) {
    this.match = match;
    this.history = history;
  }

  @action async load() {
    await this.store._compose([
      endpoints.COMPANY_DOCUMENT.with(this.match.params.id),
      endpoints.COMPANY_SIGNATURES,
      endpoints.FOLDERS.ALL,
      endpoints.CUSTOM_FIELDS.ALL,
      endpoints.USERS.ACTIVE
    ]);

    this.companyDocument = new CompanyDocument(this.store._getSingle(types.COMPANY_DOCUMENT));
    this.signatures = this.store._getAll(types.COMPANY_SIGNATURE, Signature);
    this.folders = this.store._getAll(types.FOLDER, Folder);
    this.customFields = this.store._getAll(types.CUSTOM_FIELD, CustomField);
    this.users = this.store._getAll(types.USER, User);
  }

  @action updateContent(content) {
    this.companyDocument.richText = content.html;
    this.companyDocument.lexicalState = content.state;
  }

  @action goBack() {
    this.history.push('/documents');
  }

  @action async saveCompanyDocument() {
    const {model, errors} = await this.store.patch(this.companyDocument);
    this.errors = errors;

    if (model) {
      successAlert(t('company_settings.documents.alerts.Company document saved.'));
      this.goBack();
    }
  }

  @action cancelCompanyDocumentEdit() {
    this.goBack();
  }

  @action async saveRichTextTemplate() {
    return this.saveCompanyDocument();
  }

  @action async savePdfTemplate(annotations) {
    this.companyDocument.annotations = annotations;
    await this.saveCompanyDocument();
  }

  @computed get mergeFields() {
    let customFieldMergeFields;
    customFieldMergeFields = _.chain(this.customFields)
      .filter('employeeCanView')
      .map(customField => {
        return {label: customField.name, key: `custom_field_${customField.id}`, placeholder: `#[${customField.name}]`};
      })
      .sortBy('label')
      .value();
    const documentMergeFields = mergeFields;
    if (customFieldMergeFields.length > 0) {
      documentMergeFields.slice(-1)[0].separator = true;
    }
    return documentMergeFields.concat(customFieldMergeFields);
  }

  @computed get signatureList() {
    return [Signature.employeeSignature(), ...this.signatures];
  }

  @computed get sortedFolders() {
    return _.orderBy(this.folders, 'order');
  }

  @computed get recipientOptions() {
    return [
      new NotificationRecipient({
        recipientType: 'manager'
      }),
      ...this.users.map(user => new NotificationRecipient(
        {
          recipientType: 'specific_user',
          user: user
        }
      ))
    ]
  }
}

export default CompanyDocumentEditState;
